import React, { useState, useEffect } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import '@/styles/hacker-aesthetic.css';
import '@/styles/boost-events.css';

interface BoostEvent {
  id: number;
  name: string;
  description: string;
  multiplier: string;
  duration: string;
  status: 'live' | 'upcoming' | 'ended';
}

interface BoostEventsPanelProps {
  onClose?: () => void;
}

/** Frontend season board — cosmetic until an on-chain boost contract exists. */
export const BoostEventsPanel: React.FC<BoostEventsPanelProps> = ({ onClose }) => {
  const { isConnected } = useNetwork();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<BoostEvent[]>([]);

  useEffect(() => {
    loadEvents();
  }, [isConnected]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const day = now.getUTCDay();
      const weekendBoost = day === 0 || day === 6;

      const board: BoostEvent[] = [
        {
          id: 1,
          name: 'Hash Weekend',
          description: weekendBoost
            ? 'Community spotlight weekend — activate nodes and claim before timers expire. Cosmetic board only; accrual math is unchanged.'
            : 'Returns Saturday–Sunday UTC. Prep your fleet — buy upgrades and keep nodes online. No on-chain multiplier yet.',
          multiplier: weekendBoost ? 'LIVE' : 'WEEKEND',
          duration: 'Sat–Sun UTC',
          status: weekendBoost ? 'live' : 'upcoming',
        },
        {
          id: 2,
          name: 'Chest Run',
          description: 'Habit challenge: open 3+ chests from Shop, then apply upgrades in Inventory. Does not alter rewardRate or pending accrual.',
          multiplier: 'DAILY',
          duration: 'Resets 00:00 UTC',
          status: 'live',
        },
        {
          id: 3,
          name: 'Referral Relay',
          description: 'Share your referral code. Referrers earn ETCPOWv2 on-chain when recruits buy nodes — that payout is real; this board entry is just a reminder.',
          multiplier: '1 ETCPOWv2',
          duration: 'Ongoing',
          status: 'live',
        },
        {
          id: 4,
          name: 'Leaderboard Push',
          description: 'Climb Hash Power and rewards claimed rankings. Seasonal prizes only when the Leaderboard contract is funded — board badges stay cosmetic.',
          multiplier: 'SEASON',
          duration: 'Season cycle',
          status: 'upcoming',
        },
      ];

      setEvents(board);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="be-panel">
        <div className="be-header">
          <div className="be-heading">
            <span className="be-cosmetic-badge">SEASON BOARD / COSMETIC</span>
            <div className="be-title">EVENTS</div>
          </div>
          {onClose && (
            <button className="be-close" onClick={onClose} aria-label="Close">&times;</button>
          )}
        </div>
        <div className="loading-overlay">
          <div className="hacker-loading"></div>
          <span>LOADING EVENTS...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="be-panel">
      <div className="be-header">
        <div className="be-heading">
          <span className="be-cosmetic-badge">SEASON BOARD / COSMETIC</span>
          <div className="be-title">EVENTS</div>
          <div className="be-subtitle">Community board · no accrual change</div>
        </div>
        {onClose && (
          <button className="be-close" onClick={onClose} aria-label="Close">&times;</button>
        )}
      </div>

      <p className="be-note">
        Boosts on this board are <strong>cosmetic / informational</strong> until an on-chain boost contract ships.
        They do <strong>not</strong> change VirtualNodeNFT reward accrual yet.
      </p>

      <div className="be-body">
        <div className="be-grid">
          {events.map((event) => (
            <div key={event.id} className={`be-card be-card--${event.status}`}>
              <div className="be-card-header">
                <h3 className="be-card-name">{event.name}</h3>
                <span className="be-multiplier-chip">{event.multiplier}</span>
              </div>
              <p className="be-card-desc">{event.description}</p>
              <div className="be-card-footer">
                <span className="be-duration">{event.duration}</span>
                <span className={`be-status-tag ${event.status}`}>{event.status}</span>
              </div>
              <div className="be-card-foot-note">No on-chain multiplier</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
